import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, CheckCircle, XCircle, Pencil, Save, X, Trash2 } from "lucide-react";
import { usePrioritiesStore, CitizenPriority } from "@/data/prioritiesStore";
import { ExportDropdown } from "@/components/ExportDropdown";

export default function AdminPrioritiesPage() {
  const { priorities: data, adminValidatePriority, adminEditPriority, adminDeletePriority } = usePrioritiesStore();
  const [filter, setFilter] = useState("_all_");
  const [saving, setSaving] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPriority1, setEditPriority1] = useState("");
  const [editPriority2, setEditPriority2] = useState("");

  const filtered = data.filter(p => {
    if (filter === "pending") return p.status === "en_attente";
    if (filter === "valid")   return p.status === "valide";
    if (filter === "reject")  return p.status === "rejete";
    return true;
  });

  const pending = data.filter(p => p.status === "en_attente").length;
  const valid   = data.filter(p => p.status === "valide").length;
  const rejected = data.filter(p => p.status === "rejete").length;

  async function validate(id: string, status: "valide" | "rejete") {
    setSaving(id);
    await adminValidatePriority(id, status);
    setSaving(null);
  }

  function roleBadge(p: CitizenPriority) {
    if (p.role === "ONG") return <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">ONG</span>;
    return <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">Citoyen</span>;
  }

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4">
      <div>
        <h1 className="text-2xl font-display font-black text-foreground">Attentes Citoyennes</h1>
        <p className="text-sm text-muted-foreground mt-1">Gérez et validez les attentes et priorités soumises par les citoyens et les ONG.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[{l:"En attente",v:pending,c:"text-amber-600"},{l:"Validées",v:valid,c:"text-emerald-600"},{l:"Rejetées",v:rejected,c:"text-red-500"},{l:"Total",v:data.length,c:"text-primary"}].map(x =>(
          <Card key={x.l} className="shadow-sm"><CardContent className="px-4 py-3">
            <p className={`text-2xl font-display font-black ${x.c}`}>{x.v}</p>
            <p className="text-xs text-muted-foreground font-semibold">{x.l}</p>
          </CardContent></Card>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        {[{v:"_all_",l:"Toutes"},{v:"pending",l:"En attente"},{v:"valid",l:"Validées"},{v:"reject",l:"Rejetées"}].map(f =>(
          <button key={f.v} onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === f.v ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}>{f.l}</button>
        ))}
      </div>
      <Card className="shadow-sm overflow-hidden">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-display flex items-center gap-2 text-foreground">
            <Users className="w-4 h-4" /> Liste des Priorités
          </CardTitle>
          <ExportDropdown 
            data={filtered.map(p => ({"Utilisateur": p.userName, "Rôle": p.role, "Genre": p.genre, "Tranche": p.ageGroup || "", "Priorité 1": p.priority1, "Priorité 2": p.priority2, "Soumis le": p.submittedAt, "Statut": p.status}))} 
            filename="Priorites_Citoyens" 
            title="Attentes Citoyennes" 
          />
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground font-semibold uppercase sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left">Utilisateur</th>
                  <th className="px-4 py-3 text-left">Rôle</th>
                  <th className="px-4 py-3 text-left">Genre</th>
                  <th className="px-4 py-3 text-left">Tranche</th>
                  <th className="px-4 py-3 text-left">Priorité 1</th>
                  <th className="px-4 py-3 text-left">Priorité 2</th>
                  <th className="px-4 py-3 text-left">Soumis le</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-t border-border/50 hover:bg-muted/10">
                    <td className="px-4 py-2.5 font-medium">{p.userName}</td>
                    <td className="px-4 py-2.5">{roleBadge(p)}</td>
                    <td className="px-4 py-2.5"><span className={`font-bold ${p.genre === "femme" ? "text-pink-600" : "text-blue-600"}`}>{p.genre === "femme" ? "♀" : p.genre === "homme" ? "♂" : "—"}</span></td>
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">{p.ageGroup || "—"}</td>
                    <td className="px-4 py-2.5">
                      {editingId === p.id ? (
                        <input
                          className="w-full text-xs p-1 border border-primary/40 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          value={editPriority1}
                          onChange={(e) => setEditPriority1(e.target.value)}
                        />
                      ) : (
                        <span className="font-semibold text-foreground">{p.priority1}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {editingId === p.id ? (
                        <input
                          className="w-full text-xs p-1 border border-primary/40 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          value={editPriority2}
                          onChange={(e) => setEditPriority2(e.target.value)}
                        />
                      ) : (
                        <span className="text-muted-foreground">{p.priority2 || "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">{p.submittedAt}</td>
                    <td className="px-4 py-2.5">
                      {p.status === "valide" ? <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">✓ Validée</span>
                      : p.status === "rejete" ? <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">✕ Rejetée</span>
                      : <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">⏳ Attente</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        {editingId === p.id ? (
                          <>
                            <button
                              onClick={async () => {
                                setSaving(p.id);
                                await adminEditPriority(p.id, editPriority1, editPriority2);
                                setSaving(null);
                                setEditingId(null);
                              }}
                              disabled={saving === p.id}
                              className="h-7 w-7 flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
                              title="Enregistrer"
                            >
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              disabled={saving === p.id}
                              className="h-7 w-7 flex items-center justify-center rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                              title="Annuler"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            {p.status === "en_attente" && (
                              <>
                                <button onClick={() => validate(p.id, "valide")} disabled={saving === p.id}
                                  className="h-7 w-7 flex items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors" title="Valider">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => validate(p.id, "rejete")} disabled={saving === p.id}
                                  className="h-7 w-7 flex items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 transition-colors" title="Rejeter">
                                  <XCircle className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                            <button onClick={() => {
                                setEditingId(p.id);
                                setEditPriority1(p.priority1);
                                setEditPriority2(p.priority2);
                              }}
                              disabled={saving === p.id}
                              className="h-7 w-7 flex items-center justify-center rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors" title="Modifier">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={async () => {
                                if (window.confirm("Êtes-vous sûr de vouloir supprimer cette priorité ?")) {
                                  setSaving(p.id);
                                  await adminDeletePriority(p.id);
                                  setSaving(null);
                                }
                              }}
                              disabled={saving === p.id}
                              className="h-7 w-7 flex items-center justify-center rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors" title="Supprimer">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">Aucune priorité pour ce filtre</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
